/**
 * Módulo Formulário Simples
 * Renderiza título, descrição, e-mail, select e CTA
 */

import BaseModule from './BaseModule.js';
import { normalizeFormularioSimplesData } from './helpers/formularioSimplesHelpers.js';
import { buildFormularioSimplesHTML } from './renderers/formularioSimplesRenderer.js';
import { setupFormularioSimplesHandlers } from './handlers/formularioSimplesHandlers.js';
import { getFormularioSimplesStyles } from './styles/formularioSimplesStyles.js';
import { sendMinimalDataFormData } from './utils/formApi.js';

export default class FormularioSimplesModule extends BaseModule {
    static defaultConfig = {
        container: null,
        cssPrefix: 'mzk-formulario-simples',
        color: '#00a99d',
        colorButtonText: '#ffffff',
        colorTitle: '#0f172a',
        colorText: '#475569',
        colorBackground: '#ffffff',
        fontFamily: '"Inter", sans-serif'
    };

    constructor(config = {}) {
        super({ ...FormularioSimplesModule.defaultConfig, ...config });
        this.data = null;
        this.shadow = null;
    }

    async init() {
        this.data = normalizeFormularioSimplesData(this.config);

        if (!Array.isArray(this.data.selectOptions) || this.data.selectOptions.length === 0) {
            throw new Error('FormularioSimplesModule requer selectOptions com ao menos uma opção');
        }

        // Inicializa Google Analytics
        this.initGoogleAnalytics();

        this.initialized = true;
    }

    initGoogleAnalytics() {
        if (typeof window === 'undefined') return;

        if (!window.gtag) {
            const script = document.createElement("script");
            script.async = true;
            script.src = "https://www.googletagmanager.com/gtag/js?id=G-J036VVBTZ6";
            document.head.appendChild(script);

            window.dataLayer = window.dataLayer || [];
            function gtag() { dataLayer.push(arguments); }
            window.gtag = gtag;
            window.gtag("js", new Date());
            window.gtag("config", "G-J036VVBTZ6", { send_page_view: false });
        } else {
            window.gtag("config", "G-J036VVBTZ6", { send_page_view: false });
        }

        // Track widget impression
        if (window.gtag && this.config.id) {
            window.gtag("event", "widget_impression" + this.config.id, {
                position: "mzk-widget-" + this.config.id,
                widget: this.config.titleForm || 'Formulário Simples'
            });
        }
    }

    render(container) {
        if (!this.initialized) {
            throw new Error('Módulo não foi inicializado. Chame init() primeiro.');
        }

        this.container = container || this.config.container;
        if (!this.container) {
            throw new Error('Container é obrigatório');
        }

        this.shadow = this.container.attachShadow({ mode: 'open' });

        const styleEl = document.createElement('style');
        styleEl.textContent = getFormularioSimplesStyles(this.config);
        this.shadow.appendChild(styleEl);

        const wrapper = document.createElement('div');
        wrapper.innerHTML = buildFormularioSimplesHTML(this.config, this.data);
        this.shadow.appendChild(wrapper.firstElementChild);

        this.setupEventListeners();
    }

    setupEventListeners() {
        setupFormularioSimplesHandlers(
            this.shadow,
            this.config,
            this.data,
            this.handleSubmit.bind(this)
        );
    }

    async handleSubmit(payload) {
        this.hideMessage();

        try {
            const selected = payload.selectedOption || {};
            const campaignIdentifier = selected.campaignIdentifier || payload.campaignIdentifier || payload.idcampaing || '';

            let requestPayload;
            if (this.config.isMinimalData) {
                requestPayload = {
                    email: payload.email,
                    campaignIdentifier,
                    utmCampaign: selected.slug || '',
                    utmContent: 'form-lead-ad',
                    utmMedium: 'uol',
                    utmSource: 'site-partnership',
                    utmTerm: this.config.utmTerm || null
                };
            } else {
                requestPayload = {
                    email: payload.email,
                    campaignIdentifier,
                    extraData: {
                        categoria: selected.categoria || selected.label || '',
                        origem: 'widget-minimal-data'
                    }
                };
            }

            const result = await sendMinimalDataFormData(requestPayload);
            if (!result.ok && !result.success) {
                throw new Error(result?.data?.message || 'Falha ao enviar formulário');
            }

            if (typeof this.config.onSubmit === 'function') {
                await this.config.onSubmit(requestPayload, this.data.raw);
            }

            this.showMessage(this.data.successMessage, 'success');

            const form = this.shadow.getElementById(`${this.config.cssPrefix}-form`);
            if (form) form.reset();

            this.container.dispatchEvent(new CustomEvent('formulario-simples:submit', {
                bubbles: true,
                detail: payload
            }));

            return { success: true, ok: true };
        } catch (error) {
            this.showMessage('Não foi possível enviar o formulário. Tente novamente.', 'error');
            return { success: false, ok: false };
        }
    }

    showMessage(text, type) {
        const msgBox = this.shadow?.getElementById(`${this.config.cssPrefix}-message`);
        if (!msgBox) return;

        msgBox.textContent = text;
        msgBox.className = `${this.config.cssPrefix}-message ${type}`;
    }

    hideMessage() {
        const msgBox = this.shadow?.getElementById(`${this.config.cssPrefix}-message`);
        if (!msgBox) return;

        msgBox.textContent = '';
        msgBox.className = `${this.config.cssPrefix}-message`;
    }

    static getStyles() {
        return (config) => getFormularioSimplesStyles(config);
    }

    destroy() {
        if (this.shadow) {
            this.shadow.innerHTML = '';
        }
        super.destroy();
    }
}
